import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { posApiFetch } from "@/utils/pos-api";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Loader2 } from "lucide-react";
import { drinkTypes } from "@/lib/pos/constants";

// --- Form state ---

interface DrinkComponentFormState {
  name: string;
  type: string;
  priceUsd: string;
  available: boolean;
}

const EMPTY_FORM: DrinkComponentFormState = {
  name: "",
  type: "",
  priceUsd: "",
  available: true,
};

// --- Component ---

interface CustomDrinkEditorProps {
  drinkComponentId?: string;
  onBack: () => void;
}

export default function CustomDrinkEditor({ drinkComponentId, onBack }: CustomDrinkEditorProps) {
  const isEdit = !!drinkComponentId;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<DrinkComponentFormState>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);

  // --- Fetch existing drink component for edit ---

  const { data: existingComponent, isLoading: isLoadingComponent } = useQuery<any>({
    queryKey: ["/v1/pos/drink-components", drinkComponentId],
    queryFn: async () => {
      const res = await posApiFetch(`/v1/pos/drink-components/${drinkComponentId}`);
      if (!res.ok) throw new Error("Failed to fetch drink component");
      return res.json();
    },
    enabled: isEdit,
  });

  // Populate form
  useEffect(() => {
    if (!existingComponent) return;
    const c = existingComponent;
    setForm({
      name: c.name ?? "",
      type: c.type ?? "",
      priceUsd: c.priceUsd ?? "",
      available: c.available ?? true,
    });
  }, [existingComponent]);

  // --- Field updater ---

  const set = <K extends keyof DrinkComponentFormState>(key: K, value: DrinkComponentFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // --- Submit ---

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.name.trim()) {
      toast({ title: "Validation Error", description: "Name is required", variant: "destructive" });
      return;
    }
    if (!form.type) {
      toast({ title: "Validation Error", description: "Drink type is required", variant: "destructive" });
      return;
    }
    if (!form.priceUsd || parseFloat(form.priceUsd) < 0) {
      toast({ title: "Validation Error", description: "Price must be 0 or greater", variant: "destructive" });
      return;
    }

    setIsSaving(true);
    try {
      // Backend expects multipart/form-data (multer + normalizeDrinkComponentMultipart)
      const formData = new FormData();
      formData.append("name", form.name.trim());
      formData.append("type", form.type);
      formData.append("priceUsd", form.priceUsd);
      formData.append("available", String(form.available));

      const url = isEdit ? `/v1/pos/drink-components/${drinkComponentId}` : "/v1/pos/drink-components";
      const method = isEdit ? "PUT" : "POST";

      const res = await posApiFetch(url, {
        method,
        body: formData,
        headers: {},
      });

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message || `Failed to ${isEdit ? "update" : "create"} drink component`);
      }

      toast({ title: `Drink component ${isEdit ? "updated" : "created"} successfully` });
      queryClient.invalidateQueries({ queryKey: ["/v1/pos/drink-components"] });
      onBack();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err?.message || `Failed to ${isEdit ? "update" : "create"} drink component`,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // --- Loading ---

  if (isEdit && isLoadingComponent) {
    return (
      <div className="h-full overflow-y-auto p-4">
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" onClick={onBack} className="text-[13px] gap-1">
            <ArrowLeft className="h-4 w-4" /> Back to Custom Drinks
          </Button>
        </div>
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--secondary-text-color)]" />
        </div>
      </div>
    );
  }

  // --- Render ---

  return (
    <div className="h-full overflow-y-auto p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" onClick={onBack} className="text-[13px] gap-1">
          <ArrowLeft className="h-4 w-4" /> Back to Custom Drinks
        </Button>
        <h2 className="text-[16px] font-semibold text-[var(--primary-text-color)]">
          {isEdit ? "Edit Drink Component" : "Create Drink Component"}
        </h2>
        <div className="w-[140px]" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 max-w-[900px] mx-auto pb-8">
        {/* Basic Details */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Basic Details</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[12px]">Name *</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="e.g. Vodka, Tonic Water"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">Price (USD) *</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.priceUsd}
                  onChange={(e) => set("priceUsd", e.target.value)}
                  placeholder="e.g. 5.00"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Type & Status */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Type & Status</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="space-y-1">
              <Label className="text-[12px]">Drink Type *</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger className="h-[36px] text-[13px]">
                  <SelectValue placeholder="Select drink type" />
                </SelectTrigger>
                <SelectContent>
                  {drinkTypes.map((dt) => (
                    <SelectItem key={dt.value} value={dt.value}>
                      {dt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Switch checked={form.available} onCheckedChange={(v) => set("available", v)} />
              <Label className="text-[12px]">Available</Label>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            className="h-[36px] text-[13px]"
            onClick={onBack}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="h-[36px] text-[13px] bg-[var(--primary-color)] text-white hover:bg-[#9f0e0e]"
            disabled={isSaving}
          >
            {isSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {isEdit ? "Update Component" : "Create Component"}
          </Button>
        </div>
      </form>
    </div>
  );
}
